import { FunctionComponent, useEffect, useMemo, useState } from "react"
import { WellNotesFilterI, WellNotesSortConfigI } from "./list"
import { useNavigate } from "react-router-dom"
import { debounce } from "lodash"
import styles from "./header.module.scss"
import { WELL_NOTES_ABS_ROUTE, WELL_NOTES_ADD_CHILD_ROUTE } from "../../../.."
import Button from "../../../shared/button"
import Input from "../../../shared/input"
import ReactMultiSelect, {
    ReactMultiSelectOptionT,
} from "../../../shared/input/react-select/multiSelect"
import {
    faChevronDoubleDown,
    faChevronDoubleUp,
    faChevronDown,
    faChevronUp,
} from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useBreakPointContext } from "../../../../context/breakPointContext"
import { sortArrayByKey } from "../../../../util/arrays/array"

interface WellNotesHeader {
    wellId: string
    searchTerm: string
    handleSearchTermChange: (value: string) => void
    sortConfig: WellNotesSortConfigI
    handleSortConfigChange: (config: WellNotesSortConfigI) => void
    filters: WellNotesFilterI
    onFilterChange: (filters: WellNotesFilterI) => void
    uniqueUsers: { id: string; name: string }[]
    resetAll: () => void
}

const WellNotesListHeader: FunctionComponent<WellNotesHeader> = ({
    wellId,
    searchTerm,
    sortConfig,
    filters,
    uniqueUsers,
    handleSearchTermChange,
    handleSortConfigChange,
    onFilterChange,
    resetAll,
}) => {
    // hooks
    const navigate = useNavigate()
    const breakPoint = useBreakPointContext()

    // state
    const [inputValue, setInputValue] = useState(searchTerm)
    const [showActions, setShowActions] = useState(true)

    const [createdByOptions, setCreatedByOptions] = useState<
        ReactMultiSelectOptionT[]
    >(
        uniqueUsers.map((user) => ({
            value: user.id,
            label: user.name,
            isSelected: filters.createdBy.includes(user.id),
            isDisabled: false,
        }))
    )

    // handlers
    const handleSelectClose = () => {
        const sortOpts = sortArrayByKey(
            createdByOptions,
            (opt) => (opt.isSelected ? "1" : "0"),
            false,
            "desc"
        )
        setCreatedByOptions(sortOpts)
    }

    // debounce on search change
    const debouncedSearchChange = useMemo(() => {
        return debounce((value: string) => handleSearchTermChange(value), 150)
    }, [handleSearchTermChange])

    useEffect(() => {
        return () => {
            // Cleanup when function changes
            debouncedSearchChange.cancel()
        }
    }, [debouncedSearchChange])

    /** constants */
    // determine input orientation based on small breakpoint
    // src/styles/_breakpoint.scss  && src/context/breakPointContext/index.tsx
    const isSmallBreakPoint = breakPoint === "sm"
    const inputOrientation = isSmallBreakPoint ? "horizontal" : "vertical"

    // font awesome icon
    const fontAwesomeIcon = showActions
        ? faChevronDoubleUp
        : faChevronDoubleDown

    const outerClass = isSmallBreakPoint
        ? styles.outer_small
        : styles.outer_gt_small

    // on the event of an additional added note that adds a new created by user we need to update the options to include that user.
    useEffect(() => {
        setCreatedByOptions(
            uniqueUsers.map((user) => ({
                value: user.id,
                label: user.name,
                isSelected: filters.createdBy.includes(user.id),
                isDisabled: false,
            }))
        )
    }, [uniqueUsers])

    // if we resize from small => desktop sizing we need to setShowActions to true
    // if we resize from desktop => small, show actions will be true b/c users cannot collapse actions in desktop
    useEffect(() => {
        if (!isSmallBreakPoint) {
            setShowActions(true)
        }
    }, [isSmallBreakPoint])

    return (
        <div className={outerClass}>
            {isSmallBreakPoint && (
                <Button
                    handleButtonClick={() => setShowActions(!showActions)}
                    status="primary"
                    customButtonClassName={styles.mobileDropDownContainer}
                >
                    <FontAwesomeIcon icon={fontAwesomeIcon} color="#fff" />
                </Button>
            )}
            {showActions && (
                <div>
                    <div className={styles.addNoteContainer}>
                        <Button
                            status="primary"
                            condition="default"
                            handleButtonClick={() => {
                                navigate(
                                    `${WELL_NOTES_ABS_ROUTE(
                                        wellId
                                    )}/${WELL_NOTES_ADD_CHILD_ROUTE}`
                                )
                            }}
                        >
                            Add Well Note
                        </Button>
                    </div>
                    <div className={styles.listControlContainer}>
                        <div className={styles.flexLeft}>
                            <div className={styles.inputContainer}>
                                <Input
                                    type="date"
                                    value={filters.dateRange?.startDate || ""}
                                    onChange={(e) => {
                                        onFilterChange({
                                            ...filters,
                                            dateRange: {
                                                ...filters.dateRange,
                                                startDate: e.target.value,
                                            },
                                        })
                                    }}
                                    label="From:"
                                    inputCustomClass={styles.dateInput}
                                    specializedClass="noMessage"
                                    customLabelClass={styles.inputLabel}
                                    orientation="vertical"
                                />
                            </div>
                            <div className={styles.inputContainer}>
                                <Input
                                    type="date"
                                    value={filters.dateRange?.endDate || ""}
                                    onChange={(e) => {
                                        onFilterChange({
                                            ...filters,
                                            dateRange: {
                                                ...filters.dateRange,
                                                endDate: e.target.value,
                                            },
                                        })
                                    }}
                                    orientation="vertical"
                                    label="To:"
                                    inputCustomClass={styles.dateInput}
                                    specializedClass="noMessage"
                                    customLabelClass={styles.inputLabel}
                                />
                            </div>
                            <div className={styles.multiSelectContainer}>
                                <ReactMultiSelect
                                    options={createdByOptions}
                                    onChange={(newValue) => {
                                        // update the filters for the list component to use
                                        const selectedUserIds = newValue.map(
                                            (option: ReactMultiSelectOptionT) =>
                                                option.value
                                        )
                                        onFilterChange({
                                            ...filters,
                                            createdBy: selectedUserIds,
                                        })

                                        // update the options for select
                                        const updatedOpts =
                                            createdByOptions.map((option) => ({
                                                ...option,
                                                isSelected:
                                                    selectedUserIds.includes(
                                                        option.value
                                                    ),
                                            }))
                                        setCreatedByOptions(updatedOpts)
                                    }}
                                    onMenuClose={handleSelectClose}
                                    width="220px"
                                    orientation={inputOrientation}
                                    placeholder="Filter By Created By"
                                />
                            </div>
                        </div>
                        <div className={styles.flexRight}>
                            <div className={styles.inputContainer}>
                                <Input
                                    type="text"
                                    placeHolder="Search Notes..."
                                    orientation={inputOrientation}
                                    value={inputValue}
                                    onChange={(e) => {
                                        const value = e.target.value
                                        setInputValue(value)
                                        debouncedSearchChange(value)
                                    }}
                                    specializedClass="noMessage"
                                    inputCustomClass={styles.searchInput}
                                />
                            </div>
                        </div>
                    </div>
                    <div
                        onClick={() =>
                            handleSortConfigChange({
                                field: "occurredAt",
                                direction:
                                    sortConfig.direction === "desc"
                                        ? "asc"
                                        : "desc",
                            })
                        }
                        className={styles.sortContainer}
                    >
                        <label>Sort Date:</label>
                        <FontAwesomeIcon
                            className={styles.faiSort}
                            icon={
                                sortConfig.direction === "desc"
                                    ? faChevronDown
                                    : faChevronUp
                            }
                        />
                    </div>
                    <ResetAllBtn
                        resetAll={resetAll}
                        setInputValue={setInputValue}
                    />
                </div>
            )}
        </div>
    )
}

export default WellNotesListHeader

interface ResetAllBtn {
    resetAll: () => void
    setInputValue: (value: React.SetStateAction<string>) => void
}

const ResetAllBtn: FunctionComponent<ResetAllBtn> = (props) => {
    return (
        <div className={styles.clearAll}>
            <Button
                status="secondary"
                condition="default"
                handleButtonClick={() => {
                    props.setInputValue("")
                    props.resetAll()
                }}
            >
                Reset All
            </Button>
        </div>
    )
}
